import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-white/10 flex items-center justify-center">
            <Landmark className="h-5 w-5" />
          </div>
          <span className="font-heading font-bold text-lg">Ledgerly</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight">
            Full books for<br />all your companies.
          </h1>
          <p className="mt-4 text-slate-300 max-w-md">
            Double-entry bookkeeping across your businesses — invoices, expenses, recurring
            income and time, with real P&amp;L, Balance Sheet and Alberta GST returns.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-slate-400">
            <div>
              <div className="font-heading text-2xl font-bold text-white tabular-nums">3</div>
              Companies
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-white tabular-nums">2×</div>
              Double-entry
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-white tabular-nums">5%</div>
              Alberta GST
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-500">Private tool · Alberta, Canada</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h2>
          <p className="text-slate-500 text-sm mt-1 mb-6">Sign in to your workspace.</p>

          {error && (
            <div
              data-testid="login-error"
              className="mb-4 text-sm rounded-md border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="mt-1 rounded-md"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 rounded-md"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full rounded-md bg-slate-900 hover:bg-slate-800"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
